import { useEffect } from "react";

/**
 * usePopupInteractions — 팝업(센터 모달 · 우측 드로어) 공통 이동/리사이즈 훅.
 *
 * 프로젝트의 팝업은 목업 CSS 클래스로 통일돼 있어(.scrim + .modal + .modal-head,
 * .drawer-overlay + .drawer) 개별 팝업 파일을 수정하지 않고 전역 한 곳(AppShell)에서
 * 문서 레벨 이벤트 위임으로 처리함.
 *
 * - 센터 모달(.modal): .modal-head 를 잡아 드래그 이동 + 우하단 코너를 잡아 크기 조정.
 * - 드로어(.drawer): 왼쪽 경계를 잡아 폭(너비) 리사이즈(도킹형이라 이동은 미지원).
 *
 * 반응형: 드래그/리사이즈는 데스크톱(≥1024px)에서만 활성화함. 좁은 화면에서는
 * 팝업이 이미 max-width:94vw 로 반응형 표시되므로 손대지 않고, 데스크톱에서 조작한 뒤
 * 화면이 좁아지면 커스텀 위치·크기를 리셋해 기존 반응형 레이아웃으로 되돌림.
 *
 * DOM 비침투: 리사이즈 그립 등 추가 노드를 React가 렌더한 팝업에 주입하지 않음.
 * 시각(그립/커서)은 globals.css 의 ::before/::after 로만 표현하고, 조작 판정은 좌표
 * 히트테스트로 한다(React 재조정과 충돌 없음).
 */

/** 데스크톱 판정 브레이크포인트(px) — vite-conventions 반응형 규칙과 일관. */
const DESKTOP_MIN_WIDTH = 1024;
/** 센터 모달 최소 크기(px). */
const MODAL_MIN_W = 320;
const MODAL_MIN_H = 160;
/** 드로어 최소 폭(px). */
const DRAWER_MIN_W = 360;
/** 뷰포트 대비 최대 비율(리사이즈 상한). */
const MAX_W_RATIO = 0.98;
const MAX_H_RATIO = 0.95;
/** 우하단 리사이즈 코너 히트 존(px). */
const MODAL_CORNER_ZONE = 20;
/** 드로어 왼쪽 경계 리사이즈 히트 존(px). */
const DRAWER_EDGE_ZONE = 8;
/** 뷰포트 밖으로 팝업이 완전히 사라지지 않게 유지할 여백(px). */
const KEEP_VISIBLE = 40;

/** 드래그/리사이즈를 시작하면 안 되는 인터랙티브 요소(클릭·입력 우선). */
const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, label, [role="button"], [contenteditable="true"]';

/** 조작 종류. */
type Op =
  | { kind: "modal-move"; el: HTMLElement; offsetX: number; offsetY: number }
  | {
      kind: "modal-resize";
      el: HTMLElement;
      startX: number;
      startY: number;
      startW: number;
      startH: number;
    }
  | { kind: "drawer-resize"; el: HTMLElement; startX: number; startW: number };

const clamp = (v: number, min: number, max: number): number => Math.min(Math.max(v, min), max);

const isInteractive = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null;

/** 센터 모달을 transform 센터링에서 명시 fixed left/top 좌표로 승격(최초 1회).
    팝업 컨테이너가 .modal/.new-doc/.cw-modal/.rev-flow 무엇이든 동일 센터링
    (fixed; top/left:50%; translate(-50%,-50%))이라 좌표 승격이 공통 적용됨. */
function promoteModalPosition(el: HTMLElement): void {
  if (el.dataset.eqmsMoved === "1") return;
  const rect = el.getBoundingClientRect();
  el.style.position = "fixed";
  el.style.left = `${rect.left}px`;
  el.style.top = `${rect.top}px`;
  el.style.right = "auto";
  el.style.bottom = "auto";
  el.style.transform = "none";
  el.style.margin = "0";
  el.dataset.eqmsMoved = "1";
}

export function usePopupInteractions(): void {
  useEffect(() => {
    const desktopMq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
    let op: Op | null = null;

    const endOp = () => {
      op = null;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    const onMove = (e: MouseEvent) => {
      if (!op) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (op.kind === "modal-move") {
        const w = op.el.offsetWidth;
        const left = clamp(e.clientX - op.offsetX, KEEP_VISIBLE - w, vw - KEEP_VISIBLE);
        const top = clamp(e.clientY - op.offsetY, 0, vh - KEEP_VISIBLE);
        op.el.style.left = `${left}px`;
        op.el.style.top = `${top}px`;
      } else if (op.kind === "modal-resize") {
        const w = clamp(op.startW + (e.clientX - op.startX), MODAL_MIN_W, vw * MAX_W_RATIO);
        const h = clamp(op.startH + (e.clientY - op.startY), MODAL_MIN_H, vh * MAX_H_RATIO);
        op.el.style.width = `${w}px`;
        op.el.style.height = `${h}px`;
        op.el.dataset.eqmsResized = "1";
      } else {
        // drawer-resize: 오른쪽 도킹 → 왼쪽 경계를 왼쪽으로 끌면 폭 증가
        const w = clamp(op.startW - (e.clientX - op.startX), DRAWER_MIN_W, vw * MAX_W_RATIO);
        op.el.style.width = `${w}px`;
        op.el.dataset.eqmsSized = "1";
      }
    };

    const onUp = () => endOp();

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0 || !desktopMq.matches || op) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      // 버튼·입력 등 인터랙티브 요소 클릭은 항상 우선(드래그/리사이즈 시작 금지).
      if (isInteractive(target)) return;

      // 팝업은 클래스가 여러 종류(.modal/.new-doc/.cw-modal/.rev-flow/.drawer)이므로
      // 공통 분모인 role="dialog" 로 컨테이너를 찾음.
      const dialog = target.closest<HTMLElement>('[role="dialog"]');
      if (!dialog) return;
      const rect = dialog.getBoundingClientRect();

      // 드로어(우측 도킹 aside.drawer) → 왼쪽 경계 폭 리사이즈(이동 미지원).
      if (dialog.classList.contains("drawer")) {
        if (e.clientX - rect.left <= DRAWER_EDGE_ZONE) {
          op = { kind: "drawer-resize", el: dialog, startX: e.clientX, startW: rect.width };
          startOp(e);
        }
        return;
      }

      // 센터 모달 → 우하단 코너 리사이즈.
      const nearCorner =
        rect.right - e.clientX <= MODAL_CORNER_ZONE && rect.bottom - e.clientY <= MODAL_CORNER_ZONE;
      if (nearCorner) {
        promoteModalPosition(dialog);
        op = {
          kind: "modal-resize",
          el: dialog,
          startX: e.clientX,
          startY: e.clientY,
          startW: rect.width,
          startH: rect.height,
        };
        startOp(e);
        return;
      }

      // 센터 모달 → 헤더(다이얼로그 직계 자식 *-head) 드래그 이동. CSS cursor:move 와 동일 영역.
      const header = dialog.querySelector(":scope > [class*='-head']");
      if (header && header.contains(target)) {
        promoteModalPosition(dialog);
        op = {
          kind: "modal-move",
          el: dialog,
          offsetX: e.clientX - rect.left,
          offsetY: e.clientY - rect.top,
        };
        startOp(e);
      }
    };

    const startOp = (e: MouseEvent) => {
      e.preventDefault();
      document.body.style.userSelect = "none";
      document.body.style.cursor = op?.kind === "modal-move" ? "move" : "default";
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    };

    // 화면이 좁아지면(모바일/태블릿) 커스텀 위치·크기를 리셋해 반응형 레이아웃 복원.
    const onBreakpoint = () => {
      if (desktopMq.matches) return;
      if (op) endOp();
      document
        .querySelectorAll<HTMLElement>("[data-eqms-moved], [data-eqms-resized], [data-eqms-sized]")
        .forEach((el) => {
          el.style.position = "";
          el.style.left = "";
          el.style.top = "";
          el.style.right = "";
          el.style.bottom = "";
          el.style.transform = "";
          el.style.margin = "";
          el.style.width = "";
          el.style.height = "";
          delete el.dataset.eqmsMoved;
          delete el.dataset.eqmsResized;
          delete el.dataset.eqmsSized;
        });
    };

    document.addEventListener("mousedown", onDown);
    desktopMq.addEventListener("change", onBreakpoint);
    return () => {
      document.removeEventListener("mousedown", onDown);
      desktopMq.removeEventListener("change", onBreakpoint);
      endOp();
    };
  }, []);
}
